import React, { useState, useEffect, useCallback } from 'react';
import {
  DocumentTextIcon,
  ChartBarIcon,
  CalendarIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  PlusIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
  KeyIcon,
  ServerIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import reportsService from '../services/reports';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../utils/i18nFormat';
import PageHeader from '../components/UI/PageHeader';

interface Report {
  id: string;
  name: string;
  type: 'security_events' | 'leaked_credentials' | 'digital_assets' | 'domain_investigation';
  status: 'generating' | 'completed' | 'failed' | 'scheduled';
  createdAt: string;
  completedAt?: string;
  size: string;
  format: 'pdf' | 'excel' | 'csv' | 'json';
  description: string;
  parameters: {
    dateRange: string;
    sources: string[];
    severity: string[];
  };
  data?: any;
}

interface ReportTemplate {
  id: string;
  name: string;
  type: 'security_events' | 'leaked_credentials' | 'digital_assets' | 'domain_investigation';
  description: string;
  icon: React.ReactNode;
  estimatedTime: string;
  color: string;
}

// Mover availableTemplates para fora do componente para evitar recriação
const availableTemplates: ReportTemplate[] = [
  {
    id: 'security_events',
    name: 'Relatório de Eventos de Segurança',
    type: 'security_events',
    description: 'Análise completa dos eventos de segurança com métricas e tendências',
    icon: <ShieldExclamationIcon className='h-6 w-6' />,
    estimatedTime: '3-5 minutos',
    color: 'bg-red-500',
  },
  {
    id: 'leaked_credentials',
    name: 'Credenciais Vazadas',
    type: 'leaked_credentials',
    description: 'Relatório detalhado de credenciais comprometidas encontradas',
    icon: <KeyIcon className='h-6 w-6' />,
    estimatedTime: '2-4 minutos',
    color: 'bg-orange-500',
  },
  {
    id: 'digital_assets',
    name: 'Ativos Digitais',
    type: 'digital_assets',
    description: 'Inventário e análise de ativos digitais monitorados',
    icon: <ServerIcon className='h-6 w-6' />,
    estimatedTime: '4-6 minutos',
    color: 'bg-blue-500',
  },
  {
    id: 'domain_investigation',
    name: 'Investigação de Domínio',
    type: 'domain_investigation',
    description: 'Análise forense completa de domínios suspeitos',
    icon: <MagnifyingGlassIcon className='h-6 w-6' />,
    estimatedTime: '5-8 minutos',
    color: 'bg-purple-500',
  },
];

const Reports: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplate | null>(null);
  const [selectedFormat, setSelectedFormat] = useState<'pdf' | 'excel' | 'csv' | 'json'>('pdf');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [reportData, setReportData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const { t } = useTranslation();

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      const reportsData = await reportsService.getReports();

      // Converter ReportData para Report
      const convertedReports: Report[] = reportsData.map(report => ({
        id: report.id,
        name: report.name,
        type: report.type,
        status: report.status,
        createdAt: report.created_at,
        completedAt: report.completed_at,
        size: '1.2 MB', // Valor padrão
        format: 'json' as const,
        description: t(`reports.templates.${report.type}.description`),
        parameters: {
          dateRange: t('reports.parameters.last30days'),
          sources: ['API'],
          severity: ['all'],
        },
        data: report.data,
      }));

      setReports(convertedReports);
      setTemplates(availableTemplates);
    } catch (error) {
      console.error('Erro ao carregar relatórios:', error);
      toast.error(t('reports.error.loading'));
      // Fallback para dados vazios em caso de erro
      setReports([]);
      setTemplates(availableTemplates);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className='h-5 w-5 text-green-500' />;
      case 'generating':
        return <ClockIcon className='h-5 w-5 text-yellow-500' />;
      case 'failed':
        return <ExclamationTriangleIcon className='h-5 w-5 text-red-500' />;
      default:
        return <CalendarIcon className='h-5 w-5 text-blue-500' />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'generating':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const handleCreateReport = async (format: 'json' | 'pdf' | 'csv' = 'json') => {
    if (!selectedTemplate) return;

    try {
      setGenerating(true);

      // Definir período dos últimos 30 dias
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);

      let reportData;

      if (selectedTemplate.type === 'security_events') {
        reportData = await reportsService.getSecurityEventsData({
          start: startDate.toISOString().split('T')[0],
          end: endDate.toISOString().split('T')[0],
        });
      } else if (selectedTemplate.type === 'leaked_credentials') {
        reportData = await reportsService.getLeakedCredentialsData({
          start: startDate.toISOString().split('T')[0],
          end: endDate.toISOString().split('T')[0],
        });
      } else {
        // Para outros tipos, usar dados mockados por enquanto
        reportData = {
          summary: {
            total_items: 0,
            critical_items: 0,
            high_items: 0,
            medium_items: 0,
            low_items: 0,
          },
          by_type: [],
          by_severity: [],
          by_source: [],
          timeline: [],
          recent_items: [],
        };
      }

      // Criar novo relatório com dados reais
      const newReport: Report = {
        id: `report-${Date.now()}`,
        name: `${t(`reports.templates.${selectedTemplate.type}.title`)} - ${formatDate(new Date())}`,
        type: selectedTemplate.type,
        status: 'completed',
        createdAt: new Date().toISOString(),
        completedAt: new Date().toISOString(),
        size: format === 'pdf' ? '2.5 MB' : format === 'csv' ? '850 KB' : '1.2 MB',
        format,
        description: t(`reports.templates.${selectedTemplate.type}.description`),
        parameters: {
          dateRange: t('reports.parameters.last30days'),
          sources: selectedTemplate.type === 'leaked_credentials' ? ['DeHashed', 'API'] : ['API Events', 'Dashboard'],
          severity: ['high', 'critical', 'medium', 'low'],
        },
        data: reportData,
      };

      setReports(prev => [newReport, ...prev]);
      setShowCreateModal(false);
      setSelectedTemplate(null);
      setSelectedFormat('pdf');
      toast.success(t('reports.toast.generated', { format: format.toUpperCase() }));
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      toast.error(t('reports.error.generate'));
    } finally {
      setGenerating(false);
    }
  };

  const handleViewReport = async (report: Report) => {
    try {
      if (report.data) {
        setReportData(report.data);
        setSelectedReport(report);
        setShowReportModal(true);
      } else {
        toast(t('reports.info.noData'));
      }
    } catch (error) {
      console.error('Erro ao visualizar relatório:', error);
      toast.error(t('reports.error.generate'));
    }
  };

  const handleDownloadReport = async (report: Report) => {
    try {
      if (report.data) {
        // Para relatórios de credenciais vazadas em formato CSV, usar o novo serviço
        if (report.type === 'leaked_credentials' && report.format === 'csv') {
          const filters = {
            severity: report.parameters.severity,
            sources: report.parameters.sources
          };
          await reportsService.downloadLeakedCredentialsCSV(filters);
          toast.success(t('reports.toast.csvDownloadStarted'));
          return;
        }

        let dataStr: string;
        let mimeType: string;
        let fileExtension: string;

        if (report.format === 'pdf') {
          // Gerar PDF simples usando HTML
          const htmlContent = generateHTMLReport(report);
          dataStr = htmlContent;
          mimeType = 'text/html';
          fileExtension = 'html'; // Por enquanto, gerar HTML que pode ser convertido para PDF
        } else if (report.format === 'csv') {
          // Gerar CSV
          dataStr = generateCSVReport(report);
          mimeType = 'text/csv';
          fileExtension = 'csv';
        } else {
          // JSON (padrão)
          dataStr = JSON.stringify(report.data, null, 2);
          mimeType = 'application/json';
          fileExtension = 'json';
        }

        const dataBlob = new Blob([dataStr], { type: mimeType });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${report.name.replace(/\s+/g, '_')}.${fileExtension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success(t('reports.toast.downloadStarted', { format: report.format.toUpperCase() }));
      } else {
        toast(t('reports.info.noDataForDownload'));
      }
    } catch (error) {
      console.error('Erro ao fazer download:', error);
      toast.error(t('reports.error.download'));
    }
  };

  const generateHTMLReport = (report: Report): string => {
    const data = report.data as any;
    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>${report.name}</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        .header { border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
        .summary { background: #f5f5f5; padding: 15px; border-radius: 5px; margin-bottom: 20px; }
        .section { margin-bottom: 30px; }
        .section h3 { color: #333; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
        table { width: 100%; border-collapse: collapse; margin-top: 10px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #f2f2f2; }
    </style>
</head>
<body>
    <div class="header">
        <h1>${report.name}</h1>
        <p><strong>${t('reports.labels.type')}:</strong> ${report.type}</p>
        <p><strong>${t('Created at')}:</strong> ${formatDate(report.createdAt, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
        <p><strong>${t('Period')}:</strong> ${report.parameters.dateRange}</p>
    </div>
    
    <div class="summary">
        <h2>${t('reports.metrics.general')}</h2>
        ${Object.entries(data.summary || {})
        .map(([key, value]) => `<p><strong>${key.replace(/_/g, ' ').toUpperCase()}:</strong> ${value}</p>`)
        .join('')}
    </div>
    
    ${data.by_severity
        ? `
    <div class="section">
        <h3>${t('reports.distribution.bySeverity')}</h3>
        <table>
            <tr><th>${t('Severity')}</th><th>${t('reports.labels.quantity')}</th></tr>
            ${data.by_severity.map((item: any) => `<tr><td>${item.severity}</td><td>${item.count}</td></tr>`).join('')}
        </table>
    </div>
    `
        : ''
      }
    
    ${data.recent_events || data.recent_credentials
        ? `
    <div class="section">
        <h3>${t('reports.labels.recentEvents')}</h3>
        <table>
            <tr><th>${t('reports.labels.id')}</th><th>${t('reports.labels.titleOrEmail')}</th><th>${t('Severity')}</th><th>${t('reports.labels.date')}</th></tr>
            ${(data.recent_events || data.recent_credentials || [])
          .map(
            (item: any) =>
              `<tr>
                <td>${item.id}</td>
                <td>${item.title || item.email || 'N/A'}</td>
                <td>${item.severity || 'N/A'}</td>
                <td>${item.created_at ? formatDate(item.created_at) : 'N/A'}</td>
              </tr>`,
          )
          .join('')}
        </table>
    </div>
    `
        : ''
      }
</body>
</html>`;
  };

  const generateCSVReport = (report: Report): string => {
    const data = report.data as any;
    let csv = `${t('Reports')},${report.name}\n`;
    csv += `${t('reports.labels.type')},${report.type}\n`;
    csv += `${t('Created at')},${formatDate(report.createdAt)}\n`;
    csv += `${t('Period')},${report.parameters.dateRange}\n\n`;

    // Resumo
    csv += `${t('reports.labels.summary')}\n`;
    Object.entries(data.summary || {}).forEach(([key, value]) => {
      csv += `${key.replace(/_/g, ' ')},${value}\n`;
    });
    csv += '\n';

    // Distribuição por severidade
    if (data.by_severity) {
      csv += `${t('reports.distribution.bySeverity')}\n`;
      csv += `${t('reports.distribution.bySeverity')}\n`;
      data.by_severity.forEach((item: any) => {
        csv += `${item.severity},${item.count}\n`;
      });
      csv += '\n';
    }

    // Itens recentes
    if (data.recent_events || data.recent_credentials) {
      csv += `${t('reports.labels.recentEvents')}\n`;
      csv += `${t('reports.labels.id')},${t('reports.labels.titleOrEmail')},${t('reports.labels.severity')},${t('reports.labels.date')}\n`;
      (data.recent_events || data.recent_credentials || []).forEach((item: any) => {
        csv += `${item.id},"${item.title || item.email || 'N/A'}",${item.severity || 'N/A'},${item.created_at ? formatDate(item.created_at) : 'N/A'}\n`;
      });
    }

    return csv;
  };

  if (loading) {
    return (
      <div className='space-y-6'>
        <div>
          <h1 className='text-2xl font-bold text-gray-900 dark:text-gray-100'>Relatórios</h1>
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
            {t('reports.subtitle')}
          </p>
        </div>
        <div className='flex justify-center items-center h-64'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600'></div>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <PageHeader
        title={t('Reports')}
        subtitle={t('reports.subtitle')}
      >
        <button onClick={() => setShowCreateModal(true)} className='btn btn-primary'>
          <PlusIcon className='h-5 w-5 mr-2' />
          {t('reports.new')}
        </button>
      </PageHeader>

      {/* Estatísticas */}
      <div className='grid grid-cols-1 md:grid-cols-4 gap-6'>
        <div className='card dark:bg-gray-800'>
          <div className='flex items-center'>
            <div className='flex-shrink-0'>
              <DocumentTextIcon className='h-8 w-8 text-blue-500' />
            </div>
            <div className='ml-4'>
              <p className='text-sm font-medium text-gray-500 dark:text-gray-400'>{t('reports.stats.totalReports')}</p>
              <p className='text-2xl font-semibold text-gray-900 dark:text-gray-100'>{reports.length}</p>
            </div>
          </div>
        </div>

        <div className='card dark:bg-gray-800'>
          <div className='flex items-center'>
            <div className='flex-shrink-0'>
              <CheckCircleIcon className='h-8 w-8 text-green-500' />
            </div>
            <div className='ml-4'>
              <p className='text-sm font-medium text-gray-500 dark:text-gray-400'>{t('reports.status.completed')}</p>
              <p className='text-2xl font-semibold text-gray-900 dark:text-gray-100'>
                {reports.filter(r => r.status === 'completed').length}
              </p>
            </div>
          </div>
        </div>

        <div className='card dark:bg-gray-800'>
          <div className='flex items-center'>
            <div className='flex-shrink-0'>
              <ClockIcon className='h-8 w-8 text-yellow-500' />
            </div>
            <div className='ml-4'>
              <p className='text-sm font-medium text-gray-500 dark:text-gray-400'>{t('reports.status.generating')}</p>
              <p className='text-2xl font-semibold text-gray-900 dark:text-white'>
                {reports.filter(r => r.status === 'generating').length}
              </p>
            </div>
          </div>
        </div>

        <div className='card dark:bg-gray-800'>
          <div className='flex items-center'>
            <div className='flex-shrink-0'>
              <ChartBarIcon className='h-8 w-8 text-purple-500' />
            </div>
            <div className='ml-4'>
              <p className='text-sm font-medium text-gray-500 dark:text-gray-400'>{t('reports.stats.thisMonth')}</p>
              <p className='text-2xl font-semibold text-gray-900 dark:text-white'>
                {reports.filter(r => new Date(r.createdAt).getMonth() === new Date().getMonth()).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Download Rápido de CSV */}
      <div className='card dark:bg-gray-800'>
        <div className='px-6 py-4 border-b border-gray-200'>
          <h3 className='text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center'>
            <ArrowDownTrayIcon className='h-5 w-5 mr-2 text-blue-500' />
            {t('reports.quickCsv.title')}
          </h3>
        </div>
        <div className='p-6'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            {/* Credenciais Vazadas CSV */}
            <div className='border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors'>
              <div className='flex items-center space-x-3 mb-3'>
                <div className='p-2 rounded-lg bg-orange-500 text-white'>
                  <KeyIcon className='h-5 w-5' />
                </div>
                <div>
                  <h4 className='text-lg font-medium text-gray-900 dark:text-gray-100'>{t('reports.templates.leaked_credentials.title')}</h4>
                  <p className='text-sm text-gray-500 dark:text-gray-400'>{t('reports.quickCsv.directCsv')}</p>
                </div>
              </div>
              <div className='space-y-3'>
                <div className='bg-blue-50 border border-blue-200 rounded-md p-3 mb-3'>
                  <p className='text-sm text-blue-700'>
                    <span className='font-medium'>{t('reports.autoFilter.label')}</span> {t('reports.autoFilter.byEmailDomain')}
                  </p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      await reportsService.downloadLeakedCredentialsCSV();
                      toast.success(t('reports.toast.csvDownloadStarted'));
                    } catch (error) {
                      console.error('Erro ao baixar CSV:', error);
                      toast.error(t('reports.error.csvDownload'));
                    }
                  }}
                  className='w-full bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 transition-colors flex items-center justify-center'
                >
                  <ArrowDownTrayIcon className='h-4 w-4 mr-2' />
                  {t('Download CSV')}
                </button>
              </div>
            </div>

            {/* Estatísticas de Credenciais */}
            <div className='border border-gray-200 rounded-lg p-4 hover:border-green-300 transition-colors'>
              <div className='flex items-center space-x-3 mb-3'>
                <div className='p-2 rounded-lg bg-green-500 text-white'>
                  <ChartBarIcon className='h-5 w-5' />
                </div>
                <div>
                  <h4 className='text-lg font-medium text-gray-900 dark:text-gray-100'>{t('View Statistics')}</h4>
                  <p className='text-sm text-gray-500 dark:text-gray-400'>{t('View Statistics')}</p>
                </div>
              </div>
              <div className='space-y-3'>
                <div className='bg-green-50 border border-green-200 rounded-md p-3 mb-3'>
                  <p className='text-sm text-green-700'>
                    <span className='font-medium'>{t('reports.autoFilter.label')}</span> {t('reports.autoFilter.statsByEmailDomain')}
                  </p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const stats = await reportsService.getLeakedCredentialsStats();

                      // Mostrar estatísticas em um toast ou modal
                      const message = `${t('reports.stats.total')}: ${stats.total_credentials} | ${t('reports.stats.unique')}: ${stats.unique_emails} | ${t('reports.stats.domains')}: ${stats.unique_domains}`;
                      toast(message);
                    } catch (error) {
                      console.error('Erro ao obter estatísticas:', error);
                      toast.error(t('reports.error.generate'));
                    }
                  }}
                  className='w-full bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors flex items-center justify-center'
                >
                  <ChartBarIcon className='h-4 w-4 mr-2' />
                  {t('View Statistics')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Relatórios */}
      <div className='card dark:bg-gray-800'>
        <div className='px-6 py-4 border-b border-gray-200'>
          <h3 className='text-lg font-medium text-gray-900 dark:text-gray-100'>{t('reports.recent')}</h3>
        </div>
        <div className='divide-y divide-gray-200'>
          {reports.map(report => (
            <div key={report.id} className='p-6'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center space-x-4'>
                  <div className='flex-shrink-0'>{getStatusIcon(report.status)}</div>
                  <div>
                    <h4 className='text-lg font-medium text-gray-900 dark:text-gray-100'>{report.name}</h4>
                    <p className='text-sm text-gray-500 dark:text-gray-400'>{report.description}</p>
                    <div className='mt-2 flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400'>
                      <span>{t('Created at')}: {formatDate(report.createdAt, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                      {report.completedAt && (
                        <span>{t('Completed at')}: {formatDate(report.completedAt, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                      )}
                      <span>{t('Size')}: {report.size}</span>
                      <span>{t('Format')}: {report.format.toUpperCase()}</span>
                    </div>
                  </div>
                </div>
                <div className='flex items-center space-x-4'>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(report.status)}`}>
                    {report.status === 'completed' ? t('reports.status.completed') :
                      report.status === 'generating' ? t('reports.status.generating') :
                        report.status === 'failed' ? t('reports.status.failed') : t('reports.status.scheduled')}
                  </span>
                  <div className='flex items-center space-x-2'>
                    {report.status === 'completed' && (
                      <>
                        <button
                          onClick={() => handleViewReport(report)}
                          className='p-2 text-gray-400 hover:text-gray-600'
                          title={t('View')}
                        >
                          <EyeIcon className='h-5 w-5' />
                        </button>
                        <button
                          onClick={() => handleDownloadReport(report)}
                          className='p-2 text-gray-400 hover:text-gray-600'
                          title={t('Download')}
                        >
                          <ArrowDownTrayIcon className='h-5 w-5' />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Parâmetros do relatório */}
              <div className='mt-4 grid grid-cols-3 gap-4 text-sm'>
                <div>
                  <span className='font-medium text-gray-700 dark:text-gray-300'>{t('Period')}:</span>
                  <p className='text-gray-600 dark:text-gray-400'>{report.parameters.dateRange}</p>
                </div>
                <div>
                  <span className='font-medium text-gray-700 dark:text-gray-300'>{t('Sources')}:</span>
                  <p className='text-gray-600 dark:text-gray-400'>{report.parameters.sources.join(', ')}</p>
                </div>
                <div>
                  <span className='font-medium text-gray-700 dark:text-gray-300'>{t('Severity')}:</span>
                  <p className='text-gray-600 dark:text-gray-400'>{report.parameters.severity.join(', ')}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal de Criação de Relatório */}
      {showCreateModal && (
        <div className='fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50'>
          <div className='relative top-20 mx-auto p-5 border w-2/3 shadow-lg rounded-md bg-white dark:bg-gray-800 dark:border-gray-600'>
            <div className='mt-3'>
              <h3 className='text-lg font-medium text-gray-900 dark:text-gray-100 mb-6'>{t('reports.createModal.title')}</h3>

              <div className='grid grid-cols-2 gap-6'>
                {templates.map(template => (
                  <div
                    key={template.id}
                    className={`p-6 border-2 rounded-lg cursor-pointer transition-colors ${selectedTemplate?.id === template.id
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400'
                      : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                      }`}
                    onClick={() => setSelectedTemplate(template)}
                  >
                    <div className='flex items-center space-x-4'>
                      <div className={`p-2 rounded-lg ${template.color} text-white`}>{template.icon}</div>
                      <div>
                        <h4 className='text-lg font-medium text-gray-900 dark:text-gray-100'>{t(`reports.templates.${template.type}.title`)}</h4>
                        <p className='text-sm text-gray-500 dark:text-gray-400'>{t(`reports.templates.${template.type}.description`)}</p>
                        <p className='text-xs text-gray-400 dark:text-gray-500 mt-2'>
                          {t('reports.estimatedLabel')} {t(`reports.templates.${template.type}.estimatedTime`)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {selectedTemplate && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">{t('reports.createModal.settings')}</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>{t('reports.createModal.periodLabel')}</label>
                      <select className='mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100'>
                        <option>{t('reports.createModal.period.days7')}</option>
                        <option>{t('reports.createModal.period.days30')}</option>
                        <option>{t('reports.createModal.period.days90')}</option>
                        <option>{t('reports.createModal.period.custom')}</option>
                      </select>
                    </div>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>{t('reports.createModal.formatLabel')}</label>
                      <select
                        value={selectedFormat}
                        onChange={e => setSelectedFormat(e.target.value as 'pdf' | 'excel' | 'csv' | 'json')}
                        className='mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                      >
                        <option value='pdf'>PDF</option>
                        <option value='excel'>Excel</option>
                        <option value='csv'>CSV</option>
                        <option value='json'>JSON</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              <div className='flex justify-end space-x-3 mt-6'>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setSelectedTemplate(null);
                    setSelectedFormat('pdf');
                  }}
                  className='px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-400 dark:hover:bg-gray-500'
                >
                  {t('Cancel')}
                </button>
                <button
                  onClick={() =>
                    handleCreateReport(selectedFormat === 'excel' ? 'csv' : (selectedFormat as 'json' | 'pdf' | 'csv'))
                  }
                  disabled={!selectedTemplate || generating}
                  className='px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  {generating ? t('Generating...') : t('reports.generate')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Visualização do Relatório */}
      {showReportModal && selectedReport && reportData && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'>
          <div className='bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden'>
            <div className='p-6 border-b border-gray-200 dark:border-gray-700'>
              <div className='flex items-center justify-between'>
                <h3 className='text-lg font-medium text-gray-900 dark:text-gray-100'>{selectedReport.name}</h3>
                <button
                  onClick={() => setShowReportModal(false)}
                  className='text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                >
                  <svg className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                  </svg>
                </button>
              </div>
            </div>

            <div className='p-6 overflow-y-auto max-h-[calc(90vh-120px)]'>
              {selectedReport.type === 'security_events' && (
                <div className='space-y-6'>
                  {/* Métricas Gerais */}
                  <div>
                    <h4 className='text-lg font-medium text-gray-900 dark:text-gray-100 mb-4'>{t('reports.metrics.general')}</h4>
                    <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
                      <div className='bg-gray-50 dark:bg-gray-700 p-4 rounded-lg'>
                        <div className='text-2xl font-bold text-gray-900 dark:text-gray-100'>
                          {reportData.summary.total_events}
                        </div>
                        <div className='text-sm text-gray-500 dark:text-gray-400'>{t('reports.metrics.totalEvents')}</div>
                      </div>
                      <div className='bg-gray-50 dark:bg-gray-700 p-4 rounded-lg'>
                        <div className='text-2xl font-bold text-red-600'>{reportData.summary.critical_events}</div>
                        <div className='text-sm text-gray-500 dark:text-gray-400'>{t('reports.metrics.critical')}</div>
                      </div>
                      <div className='bg-gray-50 dark:bg-gray-700 p-4 rounded-lg'>
                        <div className='text-2xl font-bold text-orange-600'>{reportData.summary.high_events}</div>
                        <div className='text-sm text-gray-500 dark:text-gray-400'>{t('reports.metrics.highSeverity')}</div>
                      </div>
                      <div className='bg-gray-50 dark:bg-gray-700 p-4 rounded-lg'>
                        <div className='text-2xl font-bold text-blue-600'>{reportData.summary.new_events}</div>
                        <div className='text-sm text-gray-500 dark:text-gray-400'>{t('reports.metrics.new')}</div>
                      </div>
                    </div>
                  </div>

                  {/* Distribuição por Tipo */}
                  <div>
                    <h4 className='text-lg font-medium text-gray-900 dark:text-gray-100 mb-4'>{t('reports.distribution.byType')}</h4>
                    <div className='space-y-2'>
                      {reportData.by_type.map((item: any) => (
                        <div
                          key={item.type}
                          className='flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg'
                        >
                          <span className='text-gray-900 dark:text-gray-100 capitalize'>{item.type}</span>
                          <span className='font-medium text-gray-900 dark:text-gray-100'>{item.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Distribuição por Severidade */}
                  <div>
                    <h4 className='text-lg font-medium text-gray-900 dark:text-gray-100 mb-4'>
                      {t('reports.distribution.bySeverity')}
                    </h4>
                    <div className='space-y-2'>
                      {reportData.by_severity.map((item: any) => (
                        <div
                          key={item.severity}
                          className='flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg'
                        >
                          <span className='text-gray-900 dark:text-gray-100 capitalize'>{item.severity}</span>
                          <span className='font-medium text-gray-900 dark:text-gray-100'>{item.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Eventos Recentes */}
                  <div>
                    <h4 className='text-lg font-medium text-gray-900 dark:text-gray-100 mb-4'>{t('reports.labels.recentEvents')}</h4>
                    <div className='space-y-3'>
                      {reportData.recent_events.slice(0, 5).map((event: any) => (
                        <div key={event.id} className='p-4 border border-gray-200 dark:border-gray-600 rounded-lg'>
                          <div className='flex items-start justify-between'>
                            <div className='flex-1'>
                              <h5 className='font-medium text-gray-900 dark:text-gray-100'>{event.title}</h5>
                              <div className='flex items-center space-x-4 mt-2 text-xs text-gray-400 dark:text-gray-500'>
                                <span>{t('reports.labels.type')}: {event.type}</span>
                                <span>{t('reports.labels.severity')}: {event.severity}</span>
                                <span>{t('reports.labels.source')}: {event.source}</span>
                                <span>{formatDate(event.created_at)}</span>
                              </div>
                            </div>
                            <span
                              className={`px-2 py-1 text-xs rounded-full ${event.severity === 'critical'
                                ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                : event.severity === 'high'
                                  ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                                  : event.severity === 'medium'
                                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                    : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                }`}
                            >
                              {event.severity}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className='p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3'>
              <button
                onClick={() => handleDownloadReport(selectedReport)}
                className='px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700'
              >
                {t('Download')} JSON
              </button>
              <button
                onClick={() => setShowReportModal(false)}
                className='px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500'
              >
                {t('Cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
